import {
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_EFFECT_KIND,
  LIGHTING_TARGET,
  type BaseLightingTarget,
  type LightingEffectDefinition,
} from '@rapoo/device-sdk'

function createBaseEffect(
  target: BaseLightingTarget,
  effect: Omit<LightingEffectDefinition, 'target'>,
): LightingEffectDefinition {
  return {
    ...effect,
    target,
  }
}

function createBaseEffects(
  target: BaseLightingTarget,
  effects: Omit<LightingEffectDefinition, 'target'>[],
): LightingEffectDefinition[] {
  return effects.map((effect) => createBaseEffect(target, effect))
}

/** ESK900 系列键盘主灯灯效，集中维护同系列固件灯效能力。 */
export const esk900KeyboardLightingEffects: LightingEffectDefinition[] = [
  {
    id: 100,
    i18nKey: 'lighting.effect.wave_flow',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 101,
    i18nKey: 'lighting.effect.colorful_raindrop_falling',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 102,
    i18nKey: 'lighting.effect.breathing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 103,
    i18nKey: 'lighting.effect.stars_twinkle',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 104,
    i18nKey: 'lighting.effect.ripple',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 105,
    i18nKey: 'lighting.effect.shadow_chasing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0,
    i18nKey: 'lighting.effect.rgb_constant',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    staticType: 1,
  },
  {
    id: 1,
    i18nKey: 'lighting.effect.blue_sky_white_clouds',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.row_gradient',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Gradient,
    ],
    staticType: 3,
  },
  {
    id: 3,
    i18nKey: 'lighting.effect.col_gradient',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Gradient,
    ],
    staticType: 2,
  },
  {
    id: 4,
    i18nKey: 'lighting.effect.moba',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
]

/** ESK900-75RT 侧灯灯效。 */
export const esk900SideLightingEffects: LightingEffectDefinition[] = [
  {
    id: 0,
    i18nKey: 'lighting.effect.rgb_flow',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Speed],
  },
  {
    id: 1,
    i18nKey: 'lighting.effect.static',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.static_breathing',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
]

/** ESK800-75RT 键盘主灯灯效。 */
export const esk800KeyboardLightingEffects: LightingEffectDefinition[] = [
  {
    id: 0x08,
    i18nKey: 'lighting.effect.click_diffusion',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x09,
    i18nKey: 'lighting.effect.static',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0a,
    i18nKey: 'lighting.effect.seeking_plum_blossoms',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0b,
    i18nKey: 'lighting.effect.breathing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0c,
    i18nKey: 'lighting.effect.ripple',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0d,
    i18nKey: 'lighting.effect.wave_flow',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0e,
    i18nKey: 'lighting.effect.colorful_raindrop_falling',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x0f,
    i18nKey: 'lighting.effect.horse_race_lamp',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x10,
    i18nKey: 'lighting.effect.sinusoid',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x11,
    i18nKey: 'lighting.effect.spiral',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x12,
    i18nKey: 'lighting.effect.fill',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x13,
    i18nKey: 'lighting.effect.stacks',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0x14,
    i18nKey: 'lighting.effect.swap',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
]

const p8BaseEffects: Omit<LightingEffectDefinition, 'target'>[] = [
  {
    id: 1,
    i18nKey: 'lighting.effect.spectrum_cycle',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.magic_color',
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.ColorCycle],
  },
  {
    id: 3,
    i18nKey: 'lighting.effect.static',
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
  },
  {
    id: 4,
    i18nKey: 'lighting.effect.static_breathing',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Speed, LIGHTING_EDITABLE_FIELD.Color],
  },
  {
    id: 5,
    i18nKey: 'lighting.effect.color_breathing',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Speed, LIGHTING_EDITABLE_FIELD.ColorCycle],
  },
  {
    id: 6,
    i18nKey: 'lighting.effect.battery_indicator',
    kind: LIGHTING_EFFECT_KIND.Indicator,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    tip: 'mouseBatteryPriority',
  },
]

const p5BaseEffects: Omit<LightingEffectDefinition, 'target'>[] = [
  {
    id: 1,
    i18nKey: 'lighting.effect.spectrum_cycle',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.magic_color',
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.ColorCycle],
  },
  {
    id: 3,
    i18nKey: 'lighting.effect.stars_twinkle',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 4,
    i18nKey: 'lighting.effect.static_breathing',
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 5,
    i18nKey: 'lighting.effect.static',
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
  },
  {
    id: 6,
    i18nKey: 'lighting.effect.row_gradient',
    kind: LIGHTING_EFFECT_KIND.Gradient,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Gradient,
    ],
  },
]

export const p8BaseLightBandEffects = createBaseEffects(
  LIGHTING_TARGET.BaseLightBand,
  p8BaseEffects,
)
export const p8BaseLightScreenEffects = createBaseEffects(
  LIGHTING_TARGET.BaseLightScreen,
  p8BaseEffects,
)
export const p5BaseLightBandEffects = createBaseEffects(
  LIGHTING_TARGET.BaseLightBand,
  p5BaseEffects,
)

/**
 * V700 灯效。
 */
export const v700KeyboardEffects: LightingEffectDefinition[] = [
  {
    id: 100,
    i18nKey: 'lighting.effect.wave_flow',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 101,
    i18nKey: 'lighting.effect.colorful_raindrop_falling',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 102,
    i18nKey: 'lighting.effect.breathing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 103,
    i18nKey: 'lighting.effect.stars_twinkle',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 104,
    i18nKey: 'lighting.effect.ripple',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 105,
    i18nKey: 'lighting.effect.swiftly',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 106,
    i18nKey: 'lighting.effect.sudden_turn',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 107,
    i18nKey: 'lighting.effect.great_winged',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 108,
    i18nKey: 'lighting.effect.seeking_plum_blossoms',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 109,
    i18nKey: 'lighting.effect.snap',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 110,
    i18nKey: 'lighting.effect.click_diffusion',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
  },
  {
    id: 111,
    i18nKey: 'lighting.effect.twinkling_stars',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
  },
  {
    id: 0,
    i18nKey: 'lighting.effect.rgb_constant',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.ColorCycle],
    staticType: 1,
  },
  {
    id: 1,
    i18nKey: 'lighting.effect.blue_sky_white_clouds',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.row_gradient',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Gradient,
    ],
    staticType: 3,
  },
  {
    id: 3,
    i18nKey: 'lighting.effect.col_gradient',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Gradient,
    ],
    staticType: 2,
  },
  {
    id: 4,
    i18nKey: 'lighting.effect.moba',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
  {
    id: 5,
    i18nKey: 'lighting.effect.fps',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
  {
    id: 6,
    i18nKey: 'lighting.effect.car',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness],
    staticType: 1,
  },
]

// ===== GCOME_AP 磁轴键盘灯效（协议定义 0-22，0xFF=关闭）=====
// 约定：
//  - 速度按 0-4 档位（功能变量区偏移 10），speedRange 告知 UI 渲染档位滑块；
//  - 除 灯光自定义(0)/光谱循环(1)/百华争艳(5)/关闭(255) 外，其余灯效同时支持
//    炫彩(ColorCycle) 与 自定义颜色(Color)。

export const gcomeApKeyboardLightingEffects: LightingEffectDefinition[] = [
  {
    // 0 灯光自定义（逐键 RGB，GAME_MODE=0）：不支持炫彩
    id: 0,
    i18nKey: 'lighting.effect.game_mode',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    isCustom: true,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 1 光谱循环：本身即彩色循环，不提供炫彩开关与自定义颜色
    id: 1,
    i18nKey: 'lighting.effect.spectrum',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 2 阶梯渐变
    id: 2,
    i18nKey: 'lighting.effect.gradient',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      // LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 3 单色常亮（可切换炫彩）
    id: 3,
    i18nKey: 'lighting.effect.static',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 4 呼吸模式
    id: 4,
    i18nKey: 'lighting.effect.breathing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 5 百华争艳：保留炫彩，不支持自定义颜色
    id: 5,
    i18nKey: 'lighting.effect.flower',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 6 横向波浪：从右向左 / 从左向右
    id: 6,
    i18nKey: 'lighting.effect.wave',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Direction,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 7 纵向波浪：从上向下 / 从下向上
    id: 7,
    i18nKey: 'lighting.effect.wave_upr',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Direction,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 8 中心扩散（喷泉）：从内向外 / 从外向内
    id: 8,
    i18nKey: 'lighting.effect.fountain',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Direction,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 9 银河波光
    id: 9,
    i18nKey: 'lighting.effect.glisten',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 10 中心回旋：逆时针 / 顺时针
    id: 10,
    i18nKey: 'lighting.effect.vortex',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.Direction,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 11 潮汐
    id: 11,
    i18nKey: 'lighting.effect.tide',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 12 摆动海浪
    id: 12,
    i18nKey: 'lighting.effect.swing',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 13 泛起涟漪
    id: 13,
    i18nKey: 'lighting.effect.ripple',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 14 常亮涟漪
    id: 14,
    i18nKey: 'lighting.effect.ripple_on',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 15 单点亮光
    id: 15,
    i18nKey: 'lighting.effect.single',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 16 宫格
    id: 16,
    i18nKey: 'lighting.effect.cell',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 17 钢琴演奏
    id: 17,
    i18nKey: 'lighting.effect.knock',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 18 流光
    id: 18,
    i18nKey: 'lighting.effect.across',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 19 落雨
    id: 19,
    i18nKey: 'lighting.effect.rain',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 20 星光
    id: 20,
    i18nKey: 'lighting.effect.starry',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 21 烟花
    id: 21,
    i18nKey: 'lighting.effect.firework',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 22 波带
    id: 22,
    i18nKey: 'lighting.effect.wave_bar',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Color,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 255 关闭
    id: 255,
    i18nKey: 'lighting.effect.off',
    target: LIGHTING_TARGET.Keyboard,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [],
  },
]

/**
 * GCOME_AP 侧灯专用灯效（网页版抓包确认：侧灯模式为独立编码 0-4）：
 *   0=流光, 1=七彩循环, 2=呼吸, 3=常亮, 4=关闭
 */
export const gcomeApSideLightingEffects: LightingEffectDefinition[] = [
  {
    // 0 流光
    id: 0,
    i18nKey: 'lighting.effect.rgb_flow',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 1 七彩循环
    id: 1,
    i18nKey: 'lighting.effect.spectrum',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    speedRange: { min: 0, max: 4, step: 1 },
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 2 呼吸
    id: 2,
    i18nKey: 'lighting.effect.static_breathing',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Color,
      LIGHTING_EDITABLE_FIELD.ColorCycle,
    ],
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  {
    // 3 常亮
    id: 3,
    i18nKey: 'lighting.effect.static',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Static,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
    brightnessRange: { min: 0, max: 100, step: 25 },
  },
  // {
  //   // 4 关闭
  //   id: 4,
  //   i18nKey: 'lighting.effect.off',
  //   target: LIGHTING_TARGET.Side,
  //   kind: LIGHTING_EFFECT_KIND.Static,
  //   editableFields: [],
  // },
]

/**
 * GCOME_AP 音乐律动灯效（软件渲染，对应 audio-rhythm 灯光模式七项）。
 * modeKey 与 SDK AudioRhythmMode 对齐，供律动引擎选用算法。
 */
export const gcomeApAudioRhythmLightingEffects: LightingEffectDefinition[] = [
  {
    id: 0,
    i18nKey: 'lighting.effect.spectrum_bar',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'spectrumBar',
    editableFields: [],
  },
  {
    id: 1,
    i18nKey: 'lighting.effect.central_pulse',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'centralPulse',
    editableFields: [],
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.peak_meter',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'peakMeter',
    editableFields: [],
  },
  {
    id: 3,
    i18nKey: 'lighting.effect.scan_line',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'scanLine',
    editableFields: [],
  },
  {
    id: 4,
    i18nKey: 'lighting.effect.audio_wave',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'wave',
    editableFields: [],
  },
  {
    id: 5,
    i18nKey: 'lighting.effect.flame',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'flame',
    editableFields: [],
  },
  {
    id: 6,
    i18nKey: 'lighting.effect.beat_breathe',
    target: LIGHTING_TARGET.AudioRhythm,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    modeKey: 'beatBreathe',
    editableFields: [],
  },
]

/** V700 侧灯灯效。 */
export const V700SideLightingEffects: LightingEffectDefinition[] = [
  {
    id: 0,
    i18nKey: 'lighting.effect.static',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
  },
  {
    id: 1,
    i18nKey: 'lighting.effect.rgb_flow',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [
      LIGHTING_EDITABLE_FIELD.ColorCycle,
      LIGHTING_EDITABLE_FIELD.Brightness,
      LIGHTING_EDITABLE_FIELD.Speed,
    ],
  },
  {
    id: 2,
    i18nKey: 'lighting.effect.static_breathing',
    target: LIGHTING_TARGET.Side,
    kind: LIGHTING_EFFECT_KIND.Dynamic,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
  },
]

/** V700 rgb指示灯效。 */
export const V700IndicatorEffects: LightingEffectDefinition[] = [
  {
    id: 1,
    i18nKey: 'lighting.effect.ambient',
    target: LIGHTING_TARGET.Indicator,
    kind: LIGHTING_EFFECT_KIND.Ambient,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
    tip: 'lighting.effect.ambientTip',
  },
  {
    id: 0,
    i18nKey: 'lighting.effect.indicator',
    target: LIGHTING_TARGET.Indicator,
    kind: LIGHTING_EFFECT_KIND.Indicator,
    editableFields: [LIGHTING_EDITABLE_FIELD.Brightness, LIGHTING_EDITABLE_FIELD.Color],
    tip: 'lighting.effect.indicatorTip',
  },
]
